/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import React, {Component, useEffect} from 'react';
import {Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import {AppContextProvider} from './AppContext';
import NotFoundPage from './components/NotFoundPage/NotFoundPage';
import {apiGet} from './components/service/service';
import LandingPage from './components/LandingPage/LandingPage';
import Header from './components/Header/Header';
import ErrorPage from './components/Error/ErrorPage';

const ExternalRedirect = ({url}) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.open(url, "_blank");
    navigate('/');
  }, [url, navigate]);

  return null;
};

class App extends Component {
  state = {
    navigationIsExpanded: true,
    dropdownIsOpen: false,
    isLoadingConfig: true,
    shouldRender: false
  };

  async componentDidMount() {
    await this.getConfig();
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      const newPath = this.props.location.pathname;
      const isStaticRoute = newPath === '/err' || newPath === '/notFound';
      if (isStaticRoute) {
        await this.asyncSetState({shouldRender: true});
      } else {
        await this.asyncSetState({isLoadingConfig: true, shouldRender: false});
        await this.getConfig();
      }
    }
  }

  asyncSetState(state) {
    return new Promise((resolve) => {
      this.setState(state, resolve);
    });
  }

  async getConfig() {
    try {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const lastSegment = pathSegments[pathSegments.length - 1] || '';
      const isCurrentPathStatic = lastSegment === 'err' || lastSegment === 'notFound';

      // Check if the route is /:appId/clear
      const isClearRoute = lastSegment === 'clear' && pathSegments.length === 2;
      const appId = isCurrentPathStatic ? '' : isClearRoute ? pathSegments[0] : lastSegment;

      // Build API URL with clear parameter if needed
      let apiUrl = '/api/config?applicationId=' + appId;
      if (isClearRoute) {
        apiUrl += '&clear=true';
      }

      const data = await apiGet(apiUrl);
      console.info(apiUrl + " returned " + data);
      const domainList = data.domainConfigs || {};
      const helpLink = data.helpLink;
      const errorResponseRoute = data.routeToUrl === '/err';
      const notFoundResponseRoute =data.routeToUrl === '/notFound';
      if (isCurrentPathStatic){
        await this.asyncSetState({domainList, helpLink, isLoadingConfig: false, shouldRender: true});
        return;
      }
      if(errorResponseRoute){
        this.props.navigate(data.routeToUrl, {
          state: {
            errorCode: 1003,
            errorMessage: 'Something is wrong with the URL provided.  Please double check the URL and try again.'
          }
        });
        return;
      }
      if(notFoundResponseRoute){
        this.props.navigate(data.routeToUrl);
        return;
      }
      const isExternal = /^https?:\/\//i.test(data.routeToUrl);
      await this.asyncSetState({domainList, helpLink, isLoadingConfig: false, shouldRender: !isExternal});
      if(isExternal){
        window.location.assign(data.routeToUrl);
      }
      else{
        this.props.navigate(data.routeToUrl);
      }
    } catch (error) {
      await this.asyncSetState({isLoadingConfig: false, shouldRender: false});
      this.props.navigate('/err', {
        state: {
          errorCode: 1001,
          errorMessage: 'Please contact the help desk, a system error has occured'
        }
      });
    }
  };

  render() {
    const path = this.props.location.pathname;
    const isStaticRoute = path === '/err' || path === '/notFound';
    if (!isStaticRoute && (this.state.isLoadingConfig || !this.state.shouldRender)) {
      return null;
    }

    return (
      <AppContextProvider value={{helpLink: this.state.helpLink, domainList: this.state.domainList}}>
        <main id='main'>
          <div className="app-container">
            <Header />
            <div>
              <Routes>
                <Route path="/askJpmc" element={<ExternalRedirect url={this.state.helpLink} />} />
                <Route path="/err" element={<ErrorPage />} />
                <Route path="/notFound" element={<NotFoundPage />} />
                <Route path="/:appId/clear" element={<LandingPage />} />
                <Route path="/:appId" element={<LandingPage />} />
                <Route path="/" element={<NotFoundPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </div>
            <footer className="footer" />
          </div>
        </main>
      </AppContextProvider>
    );
  }
}

const AppWithRouter = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  return <App {...props} navigate={navigate} location={location} />;
};

export default AppWithRouter;
